import { useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useCreateTourPlan } from '../hooks/useCreateTourPlan'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { Button, Box as DarkBox, TextInput } from '@hivewatch/dark'

import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'
import { getErrorMessage } from '@/utils/errorMessage'

import { updateCheckPointIds } from '../utils/utils'
import { DarkFloorCheckpointGroups } from './DarkFloorCheckpointGroups'
import { DarkTourPlanFormHeader } from './DarkTourPlanFormHeader'
import { footerActionsStyles } from './DarkTourPlansPanel.styles'
import { DarkTourPlansSidePanel } from './DarkTourPlansSidePanel'

interface DarkCreateTourPlanIProps {
  facilityId: string
  currentFloorId: string
  isExpanded: boolean
  onToggleExpansion: () => void
  onClose: () => void
}

export const DarkCreateTourPlan = ({
  facilityId,
  currentFloorId,
  isExpanded,
  onToggleExpansion,
  onClose,
}: DarkCreateTourPlanIProps) => {
  const colorScheme = useLightMode()
  const { t } = useTranslation()
  const me = useAppSelector(selectMe)
  const { showSuccess, showError } = useToasts()
  const { floorData, isCreateLoading, create } = useCreateTourPlan(facilityId)
  const [name, setName] = useState('')
  const [checkpointIds, setCheckpointIds] = useState<string[]>([])
  const { setHighlightedDeviceIds, setTourStopDeviceIds } = useFloorPlan()

  const isNameValid = /\S/.test(name)

  const handleChecked = (id: string) => {
    const ids = updateCheckPointIds(checkpointIds, id)
    setHighlightedDeviceIds(ids)
    setTourStopDeviceIds(ids)
    setCheckpointIds(ids)
  }

  const onSubmit = async () => {
    const input = {
      name,
      facilityId,
      checkpointIds,
    }
    try {
      await create(input)
      showSuccess(t('floors:plan_create_tour_success'))
      setTourStopDeviceIds(null)
      onClose()
      trackEvent(
        'Button Press: Facility Tour Plans - Create Tour Plan - Create',
        {
          facility_id: facilityId,
          role: me.roleName,
        }
      )
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  const handleOnClose = () => {
    onClose()
    setHighlightedDeviceIds(null)
    setTourStopDeviceIds(null)
    trackEvent(
      'Button Press: Facility Tour Plans - Create Tour Plan - Cancel',
      {
        facility_id: facilityId,
        role: me.roleName,
      }
    )
  }

  const footer = (
    <DarkBox {...footerActionsStyles}>
      <Button
        colorScheme={colorScheme}
        data-testid='tourplan_createTourPlan_cancelButton'
        onClick={handleOnClose}
        variant='tertiary'
      >
        {t('common:cancel')}
      </Button>
      <Button
        colorScheme={colorScheme}
        data-testid='tourplan_createTourPlan_createButton'
        disabled={!isNameValid || !checkpointIds.length}
        isLoading={isCreateLoading}
        onClick={onSubmit}
        variant='primary'
      >
        {t('common:create')}
      </Button>
    </DarkBox>
  )

  return (
    <DarkTourPlansSidePanel
      footer={footer}
      header={<DarkTourPlanFormHeader onClose={handleOnClose} />}
      isExpanded={isExpanded}
      onToggleExpansion={onToggleExpansion}
    >
      <TextInput
        colorScheme={colorScheme}
        data-testid='tourplan_createTourPlan_name'
        label={t('floors:plan_create_tour_name_label')}
        onValueChange={setName}
        placeholder={t('floors:plan_create_tour_name_placeholder')}
        value={name}
        width='100%'
      />
      <DarkFloorCheckpointGroups
        checkedIds={checkpointIds}
        currentFloorId={currentFloorId}
        floorData={floorData}
        onChecked={handleChecked}
      />
    </DarkTourPlansSidePanel>
  )
}
